import * as React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  Divider,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useStyles } from "../../../../Theme/Style";
import { useState, useEffect } from "react";
import { FieldArray, Form, Formik } from "formik";
import { Delete } from "@mui/icons-material";
import * as Yup from "yup";
import {
  useSubmitSublimationMutation,
  useUploadColorCodeMutation,
  useUploadLogoPlacementMutation,
  useGetSublimationQuery,
} from "../../../../services/api/production-order";
import { useNotification } from "../../../../Contexts/GlobalNotificationContext";
import { LoadingButton } from "@mui/lab";

const validationSchema = Yup.object({
  logoPlacementImage: Yup.array()
    .of(
      Yup.object().shape({
        url: Yup.string().nullable(),
        path: Yup.string().nullable(),
        file: Yup.mixed().nullable(),
      })
    )
    .min(1, "At least one image is required"),
  adultDesign: Yup.object().shape({
    url: Yup.string().nullable(),
    path: Yup.string().nullable(),
    file: Yup.mixed().nullable(),
  }),
  juniorDesign: Yup.object().shape({
    url: Yup.string().nullable(),
    path: Yup.string().nullable(),
    file: Yup.mixed().nullable(),
  }),
  comments: Yup.string().max(2000, "Comments must be at most 2000 characters"),
});

// Try to extract a storage path like "images/....jpg" from a signed URL
const guessPathFromUrl = (u) => {
  if (!u || typeof u !== "string") return "";
  try {
    const decoded = decodeURIComponent(u);
    const marker = "/images/";
    const idx = decoded.indexOf(marker);
    if (idx !== -1) {
      // return without the leading slash -> "images/.."
      return decoded.substring(idx + 1).split("?")[0];
    }
  } catch (_) {}
  return "";
};

const SublimationComponent = ({ prodId }) => {
  const classes = useStyles();
  const { addNotification } = useNotification();
  const [loading, setLoading] = useState(false);

  const [uploadColorCodes] = useUploadColorCodeMutation(); // used for adult/junior single uploads
  const [uploadLogoPlacements] = useUploadLogoPlacementMutation(); // multiple logo placements
  const [submitSublimation] = useSubmitSublimationMutation();

  const { data: sublimationData } = useGetSublimationQuery({ id: prodId });

  const [initialValues, setInitialValues] = useState({
    // Each item keeps { url (for preview), path (for submit), file }
    logoPlacementImage: [],
    adultDesign: { url: "", path: "", file: null },
    juniorDesign: { url: "", path: "", file: null },
    comments: "",
  });

  useEffect(() => {
    if (!sublimationData) return;

    // Merge logo preview URLs and paths by index
    const urlList = Array.isArray(sublimationData.logo_placement_image_urls)
      ? sublimationData.logo_placement_image_urls
      : [];
    const pathList = Array.isArray(sublimationData.logo_placement_images)
      ? sublimationData.logo_placement_images
      : [];

    const maxLen = Math.max(urlList.length, pathList.length);
    const logoPlacementImage = Array.from({ length: maxLen }, (_, i) => {
      const url = urlList[i] || "";
      const path = pathList[i] || guessPathFromUrl(url);
      return { url, path, file: null };
    });

    setInitialValues({
      logoPlacementImage,
      adultDesign: {
        url: sublimationData.adult_design_image_url || "",
        path:
          sublimationData.adult_design_image ||
          guessPathFromUrl(sublimationData.adult_design_image_url),
        file: null,
      },
      juniorDesign: {
        url: sublimationData.junior_design_image_url || "",
        path:
          sublimationData.junior_design_image ||
          guessPathFromUrl(sublimationData.junior_design_image_url),
        file: null,
      },
      comments: sublimationData.comments ?? "",
    });
  }, [sublimationData]);

  const handleArrayImageChange = (event, replace, index) => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      // For new file: preview with base64, clear path (will be set after upload)
      replace(index, { file, url: reader.result, path: "" });
    };
    reader.readAsDataURL(file);
  };

  const handleSingleImageChange = (event, setFieldValue, field) => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setFieldValue(field, { file, url: reader.result, path: "" });
    };
    reader.readAsDataURL(file);
  };

  const onSubmitData = async (values) => {
    try {
      setLoading(true);

      // 1) Upload logo placements (for those with a new file)
      const uploadLogoFiles = [];
      const logoFileIndexes = [];
      (values.logoPlacementImage || []).forEach((item, idx) => {
        if (item && item.file) {
          const formData = new FormData();
          formData.append("image", item.file);
          uploadLogoFiles.push(uploadLogoPlacements({ data: formData }).unwrap());
          logoFileIndexes.push(idx);
        }
      });

      const uploadedLogoResults = await Promise.all(uploadLogoFiles);

      // Build final logo paths preserving order:
      // - if new upload at index -> use its returned path
      // - else -> use existing path (or guess from url)
      let uploadedPtr = 0;
      const finalLogoPaths = (values.logoPlacementImage || [])
        .map((item, idx) => {
          if (item && item.file) {
            const res = uploadedLogoResults[uploadedPtr++];
            return (res && res.path) || "";
          }
          return item?.path || guessPathFromUrl(item?.url || "");
        })
        .filter(Boolean);

      // 2) Adult & Junior design: upload only if new file chosen
      const uploadSingleImage = async (obj) => {
        if (obj && obj.file) {
          const formData = new FormData();
          formData.append("image", obj.file);
          const res = await uploadColorCodes({ data: formData }).unwrap();
          return (res && res.path) ? res.path : "";
        }
        return (obj && (obj.path || guessPathFromUrl(obj.url))) || "";
      };

      const adultDesignPath = await uploadSingleImage(values.adultDesign);
      const juniorDesignPath = await uploadSingleImage(values.juniorDesign);

      // 3) Build payload — always send PATHS for backend
      const payload = {
        production_order_id: prodId,
        logo_placement_images: finalLogoPaths, // array of "images/.."
        adult_design_image: adultDesignPath,   // "images/.."
        junior_design_image: juniorDesignPath, // "images/.."
        comments: values.comments ? values.comments.trim() : "",
      };

      await submitSublimation(payload).unwrap();

      addNotification({ message: "Sublimation data saved.", type: "success" });
    } catch (e) {
      const errors = e?.data?.error
        ? Object.values(e.data.error).flat().join(", ")
        : "Something went wrong!!";
      addNotification({ message: errors, type: "error" });
      // eslint-disable-next-line no-console
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Accordion className={classes.accordion}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
        aria-controls="panel1a-content"
        id="panel1a-header"
        className={classes.accordionSummary}
      >
        <Grid container className={classes.accordionSummaryTextContainer}>
          <Grid item xs={4}>Sublimation</Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails className={classes.accordionDetails}>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={onSubmitData}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form>
              {/* Logo Placement Section */}
              <Typography variant="subtitle1" mb={2}>
                Logo Details
              </Typography>

              <FieldArray name="logoPlacementImage">
                {({ push, remove, replace }) => (
                  <>
                    <Grid container spacing={2}>
                      {(values.logoPlacementImage || []).map((_, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <input
                            accept="image/*"
                            style={{ display: "none" }}
                            id={`logo-placement-image-${index}`}
                            type="file"
                            onChange={(event) =>
                              handleArrayImageChange(event, replace, index)
                            }
                          />
                          {values.logoPlacementImage[index] &&
                            values.logoPlacementImage[index].url && (
                              <Box mt={2}>
                                <img
                                  src={values.logoPlacementImage[index].url}
                                  alt={`Logo Placement Preview ${index + 1}`}
                                  style={{
                                    width: "100%",
                                    height: 200,
                                    objectFit: "cover",
                                    borderRadius: 8,
                                  }}
                                />
                              </Box>
                            )}
                          <label htmlFor={`logo-placement-image-${index}`}>
                            <Button
                              variant="outlined"
                              component="span"
                              fullWidth
                              sx={{ mt: 1, justifyContent: "space-between" }}
                              endIcon={
                                <IconButton
                                  onClick={(e) => {
                                    e.preventDefault();
                                    remove(index);
                                  }}
                                  size="small"
                                  aria-label="remove"
                                >
                                  <Delete />
                                </IconButton>
                              }
                            >
                              Upload Logo Image {index + 1}
                            </Button>
                          </label>
                        </Grid>
                      ))}
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          onClick={() => push({ url: "", path: "", file: null })}
                        >
                          Add More Images
                        </Button>
                      </Grid>
                    </Grid>
                    {errors.logoPlacementImage && touched.logoPlacementImage && (
                      <div style={{ color: "red", marginTop: 8 }}>
                        {typeof errors.logoPlacementImage === "string"
                          ? errors.logoPlacementImage
                          : "Please add at least one image"}
                      </div>
                    )}
                  </>
                )}
              </FieldArray>

              <Divider sx={{ my: 3 }} />

              {/* Adult Design (single) */}
              <Typography variant="subtitle1" mb={2}>
                Adult Design
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="adult-design-image"
                    type="file"
                    onChange={(event) =>
                      handleSingleImageChange(event, setFieldValue, "adultDesign")
                    }
                  />
                  {values.adultDesign && values.adultDesign.url && (
                    <Box mt={2}>
                      <img
                        src={values.adultDesign.url}
                        alt="Adult Design Preview"
                        style={{
                          width: "100%",
                          height: 200,
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />
                    </Box>
                  )}
                  <label htmlFor="adult-design-image">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      sx={{ mt: 1 }}
                    >
                      Upload Adult Design
                    </Button>
                  </label>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Junior Design (single) */}
              <Typography variant="subtitle1" mb={2}>
                Junior Design
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="junior-design-image"
                    type="file"
                    onChange={(event) =>
                      handleSingleImageChange(event, setFieldValue, "juniorDesign")
                    }
                  />
                  {values.juniorDesign && values.juniorDesign.url && (
                    <Box mt={2}>
                      <img
                        src={values.juniorDesign.url}
                        alt="Junior Design Preview"
                        style={{
                          width: "100%",
                          height: 200,
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />
                    </Box>
                  )}
                  <label htmlFor="junior-design-image">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      sx={{ mt: 1 }}
                    >
                      Upload Junior Design
                    </Button>
                  </label>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Comments */}
              <Typography variant="subtitle1" mb={2}>
                Comments
              </Typography>
              <Grid item xs={12}>
                <TextField
                  label="Comments"
                  name="comments"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                  value={values.comments}
                  onChange={(e) => setFieldValue("comments", e.target.value)}
                  error={touched.comments && Boolean(errors.comments)}
                  helperText={touched.comments && errors.comments}
                />
              </Grid>

              {/* Submit */}
              <Grid item xs={12} mt={4}>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  loading={loading}
                >
                  Save
                </LoadingButton>
              </Grid>
            </Form>
          )}
        </Formik>
      </AccordionDetails>
    </Accordion>
  );
};

export default SublimationComponent;
